import {
  createContext,
  RefObject,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import type { PropsWithChildren } from 'react';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { useRoomContext } from '@livekit/components-react';
import { getOutputCameraPublication } from './cameraHealth';
import { CameraTrackRecovery } from './CameraTrackRecovery';

export const CameraRecoveryContext = createContext<
  CameraTrackRecovery | undefined
>(undefined);

export const CameraRecoveryProvider = ({ children }: PropsWithChildren) => {
  const room = useRoomContext();
  const recovery = useMemo(() => new CameraTrackRecovery(room), [room]);
  useEffect(() => {
    recovery.start();
    return () => recovery.stop();
  }, [recovery]);
  return (
    <CameraRecoveryContext.Provider value={recovery}>
      {children}
    </CameraRecoveryContext.Provider>
  );
};

const resolveCameraReference = (
  trackRef: TrackReferenceOrPlaceholder,
  recovery?: CameraTrackRecovery,
): TrackReferenceOrPlaceholder => {
  const publication = recovery
    ? getOutputCameraPublication(trackRef)
    : undefined;
  return publication &&
    !trackRef.publication &&
    (publication.isDesired || recovery?.isResetting(publication))
    ? { ...trackRef, publication }
    : trackRef;
};

// Preserve layout selection while a camera is awaiting or resetting its subscription.
export const useOutputCameraReferences = (
  trackRefs: TrackReferenceOrPlaceholder[],
): TrackReferenceOrPlaceholder[] => {
  const recovery = useContext(CameraRecoveryContext);
  return recovery
    ? trackRefs.map(ref => resolveCameraReference(ref, recovery))
    : trackRefs;
};

export const useRecoverOutputCamera = (
  trackRef: TrackReferenceOrPlaceholder,
  element: RefObject<HTMLVideoElement>,
): TrackReferenceOrPlaceholder => {
  const recovery = useContext(CameraRecoveryContext);
  const publication = recovery
    ? getOutputCameraPublication(trackRef)
    : undefined;
  const { participant } = trackRef;
  useEffect(() => {
    if (!recovery || !publication) return undefined;
    return recovery.register({
      getElement: () => element.current,
      participant,
      publication,
    });
  }, [element, participant, publication, recovery]);

  // Mount VideoTrack for an intended camera even before its subscription succeeds.
  // Without the output provider, conference and preview behaviour is unchanged.
  return resolveCameraReference(trackRef, recovery);
};
